import { useNavigate as useRouterNavigate } from 'react-router';

import { Link, SitecoreLinks, useConfig } from '@/default-config';

import {
    generateFullPath,
    generateV3Link,
    PathNavigateFunction,
    paths,
    PATHS,
    SearchKeys,
} from '@components/app/routes';
import { Dict, GetFieldType, GetPathType } from '@domain/app/Dict';
import { goToUrl } from '@utils/url-utils';

import { Logger } from './Logger';

const getLink = <T extends string>(
    links: SitecoreLinks,
    path: GetPathType<SitecoreLinks, T>,
    defaultValue?: GetFieldType<SitecoreLinks, T>,
) => {
    const link = Dict.get(links, path);

    if (link) {
        return link;
    }

    Logger.error('sitecore', `${path}: sitecore link with this path is missing in configuration`, 'useConfigLink');

    return defaultValue ?? ({ url: '' } as Link);
};

export const useConfigLink = <T extends string>(
    path: GetPathType<SitecoreLinks, T>,
    defaultValue?: GetFieldType<SitecoreLinks, T>,
) => {
    const { links } = useConfig();
    return getLink(links, path, defaultValue);
};

const generateV3MonorepoLink =
    <T extends PATHS, S = SearchKeys<T>>(path: T, links: SitecoreLinks) =>
    (...args: S extends undefined ? [] : [S]) => {
        const params = args[0] && new URLSearchParams(args[0]);

        const link = getLink(links, paths[path].link);
        return `${link.url}${params ? `?${params.toString()}` : ''}`;
    };

export const useLinkProducer = () => {
    const { links, setup } = useConfig();

    return <T extends PATHS>(path: T) =>
        (...args: SearchKeys<T> extends undefined ? [] : [SearchKeys<T>]): string => {
            if (setup.useRouter) {
                const generatedLink = generateV3Link(path)(...args);
                return generatedLink;
            }
            return generateV3MonorepoLink(path, links)(...args);
        };
};

export type LinkProducer = ReturnType<typeof useLinkProducer>;

export const useNavigate = () => {
    const { links, setup } = useConfig();
    const routerNavigate = useRouterNavigate();

    const navigate: PathNavigateFunction =
        (path, config) =>
        (...args) => {
            if (setup.useRouter) {
                const generatedPath = generateFullPath(path, config)(...args);
                routerNavigate(generatedPath);
                return;
            }
            const link = generateV3MonorepoLink(path, links)(...args);
            goToUrl(link);
        };

    return navigate;
};

export type NavigateFunction = ReturnType<typeof useNavigate>;
